import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { MapEntity } from "../components/map/mapTypes.d";
import * as markerService from "../services/markerService";
import { formatDtg } from "../utils/parseTimestamp";

interface MarkersState {
    value: MapEntity[];
    isLoading: boolean;
}

const initialState: MarkersState = {
    value: [],
    isLoading: false,
};

function toMapEntity(marker: markerService.MarkerResponse): MapEntity {
    return {
        id: marker.id,
        latLng: marker.latitude != null && marker.longitude != null
            ? { lat: marker.latitude, lng: marker.longitude }
            : { lat: 0, lng: 0 },
        code: marker.sidc,
        own: true,
        // Markers now carry their name the same way units do, so the map
        // labels them instead of showing an unlabelled symbol.
        name: marker.name,
        markerProperties: {
            ...(marker.text_modifiers ?? {}),
            ...(marker.position_time ? { dtg: formatDtg(marker.position_time) } : {}),
        },
        faction_id: marker.faction_id,
    };
}

/**
 * Fetch faction-scoped markers for the selected faction.
 */
export const fetchFactionMarkers = createAsyncThunk<
    MapEntity[],
    { eventId: string; factionId: string }
>('markers/fetchFactionMarkers', async ({ eventId, factionId }) => {
    const response = await markerService.getMarkers(eventId, factionId);
    return response.markers.map(toMapEntity);
});

/**
 * Fetch event-wide (global) markers — no faction_id filter.
 */
export const fetchGlobalMarkers = createAsyncThunk<
    MapEntity[],
    { eventId: string }
>('markers/fetchGlobalMarkers', async ({ eventId }) => {
    const response = await markerService.getMarkers(eventId);
    return response.markers.map(toMapEntity);
});

export const markersSlice = createSlice({
    name: 'markers',
    initialState,
    reducers: {
        addMarker: (state, action: PayloadAction<MapEntity>) => {
            const idx = state.value.findIndex((m) => m.id === action.payload.id);
            if (idx === -1) {
                state.value.push(action.payload);
            } else {
                const existing = state.value[idx];
                state.value[idx] = {
                    ...existing,
                    ...action.payload,
                    markerProperties: {
                        ...existing.markerProperties,
                        ...action.payload.markerProperties,
                    },
                };
            }
        },
        updateMarker: (state, action: PayloadAction<MapEntity>) => {
            const idx = state.value.findIndex((m) => m.id === action.payload.id);
            if (idx !== -1) {
                state.value[idx] = action.payload;
            }
        },
        removeMarker: (state, action: PayloadAction<string>) => {
            state.value = state.value.filter((m) => m.id !== action.payload);
        },
        clearMarkers: (state) => {
            state.value = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFactionMarkers.pending, (state) => { state.isLoading = true; })
            .addCase(fetchFactionMarkers.fulfilled, (state, action) => {
                state.isLoading = false;
                // Remove stale faction markers, then upsert fresh ones
                const incoming = new Set(action.payload.map((m) => m.id));
                state.value = state.value.filter((m) => !incoming.has(m.id));
                state.value.push(...action.payload);
            })
            .addCase(fetchFactionMarkers.rejected, (state) => { state.isLoading = false; })
            .addCase(fetchGlobalMarkers.pending, (state) => { state.isLoading = true; })
            .addCase(fetchGlobalMarkers.fulfilled, (state, action) => {
                state.isLoading = false;
                const incoming = new Set(action.payload.map((m) => m.id));
                state.value = state.value.filter((m) => !incoming.has(m.id));
                state.value.push(...action.payload);
            })
            .addCase(fetchGlobalMarkers.rejected, (state) => { state.isLoading = false; });
    },
});

export const { addMarker, updateMarker, removeMarker, clearMarkers } = markersSlice.actions;

export default markersSlice.reducer;
